import type { CSSProperties } from 'react';
import type { ColumnLayout, ElementLayout, GridPlacement, ViewportKey } from './types';
import { layoutDefinition } from './layouts';

export const GRID_COLUMNS: Record<ViewportKey, number> = { desktop: 12, tablet: 8, mobile: 4 };
export const GRID_GUTTER = 24;
export const GRID_STEP = 8;

/** One-way compatibility projection: old section columns provide the initial
 * placement only. Once materialized, element layout is the sole placement
 * authority. */
export function layoutFromColumn(sectionLayout: ColumnLayout | undefined, columnIndex: number): ElementLayout {
  const parts = layoutDefinition(sectionLayout).parts;
  const total = parts.reduce((sum, part) => sum + part, 0);
  const start = 1 + Math.round(parts.slice(0, columnIndex).reduce((sum, part) => sum + part, 0) * 12 / total);
  const end = 1 + Math.round(parts.slice(0, columnIndex + 1).reduce((sum, part) => sum + part, 0) * 12 / total);
  return { desktop: { gridStart: start, gridSpan: Math.max(1, end - start), offsetX: 0, offsetY: 0, layer: 'normal' }, mobile: { gridStart: 1, gridSpan: 4 } };
}

export function placementFor(layout: ElementLayout | undefined, viewport: ViewportKey): GridPlacement {
  const desktop: GridPlacement = layout?.desktop ?? { gridStart: 1, gridSpan: GRID_COLUMNS.desktop, offsetX: 0, offsetY: 0, layer: 'normal' };
  if (viewport === 'desktop') return desktop;

  // A viewport without an authored override must be responsive by default:
  // stack the element in the available tracks instead of carrying desktop
  // x/width/height off the edge of the narrow canvas. The vertical adjustment
  // is intentionally shared: if an owner drags a block above/below its normal
  // flow on Desktop, that ordering must not silently reverse on Mobile.
  // A viewport without an authored override must be responsive by default:
  // stack the element in the available tracks instead of carrying a desktop
  // x/y/width/height off the edge of the narrow canvas. Authored overrides
  // still replace these defaults field by field.
  const responsive: GridPlacement = {
    gridStart: 1,
    gridSpan: GRID_COLUMNS[viewport],
    offsetX: 0,
    offsetY: 0,
    layer: desktop.layer,
  };
  return { ...responsive, ...(viewport === 'tablet' ? layout?.tablet : layout?.mobile) };
}

export function clampPlacement(value: Partial<GridPlacement>, viewport: ViewportKey, current: GridPlacement): GridPlacement {
  const columns = GRID_COLUMNS[viewport];
  const gridSpan = Math.max(1, Math.min(columns, Math.round(value.gridSpan ?? current.gridSpan)));
  const gridStart = Math.max(1, Math.min(columns - gridSpan + 1, Math.round(value.gridStart ?? current.gridStart)));
  return {
    ...current,
    ...value,
    gridStart,
    gridSpan,
    // Grid tracks own the horizontal structure, but the author must still be
    // able to place an element above/below its auto-grid row. Keep a generous
    // canvas bound instead of the old 96px nudge limit, which made dragging
    // a block to the top of a section appear to be blocked by the grid.
    offsetX: Math.max(-1200, Math.min(1200, Math.round(value.offsetX ?? current.offsetX))),
    offsetY: Math.max(-1200, Math.min(1200, Math.round(value.offsetY ?? current.offsetY))),
    width: value.width === null ? null : value.width == null ? current.width : Math.max(40, Math.min(2000, Math.round(value.width))),
    height: value.height === null ? null : value.height == null ? current.height : Math.max(40, Math.min(1200, Math.round(value.height))),
  };
}

export function setPlacement(layout: ElementLayout | undefined, viewport: ViewportKey, patch: Partial<GridPlacement>): ElementLayout {
  const current = placementFor(layout, viewport);
  const next = clampPlacement(patch, viewport, current);
  if (viewport === 'desktop') return { desktop: next, tablet: layout?.tablet, mobile: layout?.mobile };
  return viewport === 'tablet'
    ? { desktop: layout?.desktop ?? placementFor(undefined, 'desktop'), tablet: next, mobile: layout?.mobile }
    : { desktop: layout?.desktop ?? placementFor(undefined, 'desktop'), tablet: layout?.tablet, mobile: next };
}

export function placementStyle(layout: ElementLayout | undefined, viewport: ViewportKey): CSSProperties {
  const desktop = placementFor(layout, 'desktop');
  const tablet = placementFor(layout, 'tablet');
  const mobile = placementFor(layout, 'mobile');
  const layer = (value: GridPlacement['layer']) => value === 'background' ? 0 : value === 'foreground' ? 2 : 1;
  return {
    '--zs-element-start': desktop.gridStart,
    '--zs-element-span': desktop.gridSpan,
    '--zs-element-x': `${desktop.offsetX}px`,
    '--zs-element-y': `${desktop.offsetY}px`,
    // Keep the Builder's base placement variables identical to the public
    // renderer. An un-authored width fills the grid area; it is not an
    // intrinsic/auto-sized box.
    '--zs-element-width': desktop.width == null ? '100%' : `${desktop.width}px`,
    '--zs-element-height': desktop.height == null ? 'auto' : `${desktop.height}px`,
    '--zs-anchor-height': desktop.height == null ? 'auto' : `${desktop.height}px`,
    '--zs-anchor-floor': '96px',
    '--zs-element-layer': layer(desktop.layer),
    '--zs-element-tablet-start': tablet.gridStart,
    '--zs-element-tablet-span': tablet.gridSpan,
    '--zs-element-tablet-x': `${tablet.offsetX}px`,
    '--zs-element-tablet-y': `${tablet.offsetY}px`,
    '--zs-element-tablet-width': tablet.width == null ? '100%' : `${tablet.width}px`,
    '--zs-element-tablet-height': tablet.height == null ? 'auto' : `${tablet.height}px`,
    '--zs-anchor-tablet-height': tablet.height == null ? 'auto' : `${tablet.height}px`,
    '--zs-element-tablet-layer': layer(tablet.layer),
    '--zs-element-mobile-start': mobile.gridStart,
    '--zs-element-mobile-span': mobile.gridSpan,
    '--zs-element-mobile-x': `${mobile.offsetX}px`,
    '--zs-element-mobile-y': `${mobile.offsetY}px`,
    '--zs-element-mobile-width': mobile.width == null ? '100%' : `${mobile.width}px`,
    '--zs-element-mobile-height': mobile.height == null ? 'auto' : `${mobile.height}px`,
    '--zs-anchor-mobile-height': mobile.height == null ? 'auto' : `${mobile.height}px`,
    '--zs-element-mobile-layer': layer(mobile.layer),
  } as CSSProperties;
}
